import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { SnackbarNotificationService } from 'ngx-lib';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { UserOrganizationStorageService } from '../services/user-organization-storage.service';

/**
 * This Cookie-Based Authentication Interceptor handles:
 * - Sending authentication cookies automatically with API requests via withCredentials
 * - Adding organization context headers to requests (when applicable)
 * - Handling authentication errors (401 Unauthorized)
 * - Managing authorization errors (403 Forbidden)
 * - Updating user organization context from server response headers
 */
export const authInterceptor: HttpInterceptorFn = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<any> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const userOrganizationStorageService = inject(UserOrganizationStorageService);
    const notificationService = inject(SnackbarNotificationService);

    const organization = userOrganizationStorageService.getUserOrganization();

    // withCredentials: true enables sending cookies with cross-origin requests
    if (shouldSendCredentials(request.url)) {
        // Determine if we should add the OrganizationId header
        // Login/public endpoints don't need it, and reporting API operates independently
        const shouldAddOrgHeader =
            !isLoginEndpoint(request.url) &&
            !isPublicEndpoint(request.url) &&
            !request.url.includes(environment.reportingApiUrl);

        request = request.clone({
            setHeaders: shouldAddOrgHeader ? { OrganizationId: organization?.id.toString() ?? '' } : {},
            withCredentials: true
        });
    }

    return next(request).pipe(
        tap((event) => {
            if (event instanceof HttpResponse && event.status === 200) {
                const organizationId = parseIntHeaderValue(event.headers.get('organizationId'));
                const userRoleId = parseIntHeaderValue(event.headers.get('userRoleId'));

                if (organizationId && userRoleId) {
                    userOrganizationStorageService.updateUserOrganization({
                        id: organizationId,
                        roleId: userRoleId
                    });
                }
            }
        }),
        catchError((error) => handleError(error, authService, router, notificationService))
    );
};

/**
 * Check if the URL is a login endpoint
 *
 * @param url The request URL to check
 * @returns true if this is a login endpoint
 */
function isLoginEndpoint(url: string): boolean {
    return url.includes('api/Login');
}

/**
 * Check if the URL is a public endpoint that doesn't require authentication
 *
 * @param url The request URL to check
 * @returns true if this is a public endpoint
 */
function isPublicEndpoint(url: string): boolean {
    const publicEndpoints = ['api/Configuration/all'];
    return publicEndpoints.some((endpoint) => url.includes(endpoint));
}

/**
 * Check if the URL is an external third-party API
 * Only send cookies to our own API domains (apiUrl and reportingApiUrl).
 *
 * @param url The request URL to check
 * @returns true if this is an external API
 */
function isExternalApi(url: string): boolean {
    return (
        url.startsWith('http') &&
        !url.includes(window.location.hostname) &&
        !url.includes(environment.reportingApiUrl) &&
        !url.includes(environment.apiUrl)
    );
}

/**
 * Determine if credentials (cookies) should be sent with the request
 * Credentials are sent to all internal APIs, excluding public endpoints and external APIs
 *
 * @param url The request URL to check
 * @returns true if cookies should be sent, false otherwise
 */
function shouldSendCredentials(url: string): boolean {
    return !isPublicEndpoint(url) && !isExternalApi(url);
}

/**
 * Logout user when authentication error occurs
 */
function handleAuthError(authService: AuthService): void {
    authService.logout();
}

/**
 * Handle HTTP errors from API requests
 *
 * @param error The HTTP error response
 * @param authService Service to handle authentication operations
 * @param router Router for navigation after errors
 * @param notificationService Service to display user-facing error messages
 * @returns Observable that re-throws the error for further handling
 */
function handleError(
    error: any,
    authService: AuthService,
    router: Router,
    notificationService: SnackbarNotificationService
): Observable<never> {
    if (error.status === 401) {
        console.warn('Authentication error - Cookie may be expired or invalid');
        handleAuthError(authService);
    } else if (error.status === 403) {
        notificationService.error('Authorization error - Insufficient permissions');
        console.warn('Authorization error - Insufficient permissions');
        router.navigate(['/forbidden']);
    } else if (error.status === 404) {
        notificationService.error('Not found - The requested resource was not found');
        console.warn('Not found - The requested resource was not found');
        router.navigate(['/not-found']);
    } else if (error.status >= 500) {
        console.error('Server error:', error.message);
    }

    return throwError(() => error);
}

/**
 * Parse an integer value from HTTP response header
 * Headers are strings, so numeric values need to be parsed.
 *
 * @param raw The raw header value (string, null, or undefined)
 * @returns Parsed integer if valid, undefined otherwise
 */
function parseIntHeaderValue(raw: string | null | undefined): number | undefined {
    const parsed = parseInt(raw ?? '', 10);
    return !isNaN(parsed) && Number.isInteger(parsed) ? parsed : undefined;
}
